import { useEffect, useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";

/** Platform admins define the tier ladder: name, commission % the tier pays, and the live-tenant count that *suggests* it (tiers are assigned manually). */
export function ResellerTiersDialog({ open, onClose, onSaved }) {
  const [tiers, setTiers] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    API.get("/platform/resellers/tiers").then(({ data }) => setTiers(data.tiers.map((t) => ({ ...t })))).catch(() => toast.error("Couldn't load tiers"));
  }, [open]);
  const set = (i, k, v) => setTiers(tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  const save = async () => {
    setBusy(true);
    try {
      const payload = tiers.map((t) => ({ ...t, key: (t.key || t.name || "").toLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "") }));
      const { data } = await API.put("/platform/resellers/tiers", { tiers: payload });
      toast.success("Tiers saved"); onSaved?.(data.tiers); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save tiers"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="tiers-dialog">
        <DialogHeader><DialogTitle>Reseller tiers</DialogTitle><DialogDescription>You pick each reseller's tier by hand; the tier sets their commission rate for future payments. The live-tenant threshold only drives the "suggested tier" hint.</DialogDescription></DialogHeader>
        {!tiers ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_90px_110px_70px_32px] gap-2 text-[10px] uppercase tracking-wider text-muted-foreground px-1"><span>Name</span><span>Commission %</span><span>Suggest at live tenants</span><span>Colour</span><span /></div>
            {tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-[1fr_90px_110px_70px_32px] gap-2 items-center" data-testid={`tier-row-${i}`}>
                <Input value={t.name} onChange={(e) => set(i, "name", e.target.value)} maxLength={40} data-testid={`tier-name-${i}`} />
                <Input type="number" step="0.5" min="0" max="100" value={t.commission_pct} onChange={(e) => set(i, "commission_pct", e.target.value)} data-testid={`tier-pct-${i}`} />
                <Input type="number" step="1" min="0" value={t.suggested_live_tenants} onChange={(e) => set(i, "suggested_live_tenants", e.target.value)} data-testid={`tier-threshold-${i}`} />
                <input type="color" value={t.color || "#64748B"} onChange={(e) => set(i, "color", e.target.value)} className="h-9 w-full rounded-md border border-input bg-background" data-testid={`tier-color-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => setTiers(tiers.filter((_, j) => j !== i))} disabled={tiers.length <= 1} data-testid={`tier-remove-${i}`}><Trash size={14} /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setTiers([...tiers, { key: "", name: "", commission_pct: 10, suggested_live_tenants: (Number(tiers[tiers.length - 1]?.suggested_live_tenants) || 0) + 10, color: "#64748B" }])} disabled={tiers.length >= 5} data-testid="tier-add"><Plus size={14} className="mr-1" /> Add tier</Button>
            <p className="text-[11px] text-muted-foreground">Up to 5 tiers, thresholds must increase. Changing a tier's rate here does not change resellers already on it — re-pick their tier to apply the new rate.</p>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy || !tiers} data-testid="tiers-save">{busy ? "Saving…" : "Save tiers"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
